import { throttle } from 'lodash';
import { ReactNode, createContext, useMemo, useEffect, useRef, useState, useContext, useCallback } from 'react';

import { kanbanJobTitleId, kanbanProficiencyLevel, kanbanSkillType, jobTitleIdForJobPostings } from '../../cache/cache';
import {
  IJobPreferencesVar,
  IJobTitleSkillSetVar,
  jobPreferencesVar,
} from '../../cache/learner/jobPreferencesReactiveVar';
import { kanbanDataVar } from '../../cache/learner/kanbanReactiveVar';
import { learningPathsByInstitutionVar } from '../../cache/learner/learningPathsByInstitutionReactiveVar';
import { learningPathsByObjectivesVar } from '../../cache/learner/learningPathsByObjectivesReactiveVar';
import { RecommendedGoalsBySpecializedMajorsReactiveVar } from '../../cache/learner/recommendedGoalsBySpecializedMajorsReactiveVar';
import { RecommendedOccupationsByMajorReactiveVar } from '../../cache/learner/recommendedOccupationsByMajorReactiveVar';
import { RecommendedOccupationsReactiveVar } from '../../cache/learner/recommendedOccupationsReactiveVar';
import { recommendedSkillsVar } from '../../cache/learner/recommendedSkillsReactiveVar';
import { RelatedOccupationsReactiveVar } from '../../cache/learner/relatedOccupationsReactiveVar';
import { studentLearningPathsVar } from '../../cache/learner/studentLearningPathsReactiveVar';
import { FeaturesEnum } from '../../constants/enums/FeaturesEnum';
import useJobPostingRecomnmendationHook from '../../hooks/jobPostingRecommendationHook/useJobPostingRecommendationHook';
import useJobPreferencesDataHook from '../../hooks/jobPreferencesDataHook/useJobPreferencesDataHook';
import useKanbanDataHook from '../../hooks/kanbanDataHook/useKanbanDataHook';
import { useLearningPathsByInstitutionHook } from '../../hooks/learningPathsByInstitutionHook/useLearningPathsByInstitutionHook';
import { useLearningPathsByObjectivesHook } from '../../hooks/learningPathsByObjectivesHook/useLearningPathsByObjectivesHook';
import { fetchRecommendedGoalsBySpecializedMajors } from '../../hooks/recommendedGoalsBySpecializedMajorsHook/fetchRecommendedGoalsBySpecializedMajors';
import RecommendedGoalsBySpecializedMajorsHook from '../../hooks/recommendedGoalsBySpecializedMajorsHook/recommendedGoalsBySpecializedMajorsHook';
import RecommendedOccupationsByMajorHook from '../../hooks/recommendedOccupationsByMajorHook/recommendedOccupationsByMajorHook';
import RecommendedOccupationHook from '../../hooks/RecommendedOccupationsHook/recommendedOccupationsHook';
import useRecommendedSkillsHook from '../../hooks/recommendedSkillsHook/useRecommendedSkillsHook';
import SkillForCoursesRecommendationHook from '../../hooks/skillForCoursesRecommendationHook/skillForCoursesRecommendationHook';
import useStudentLearningPathsHook from '../../hooks/studentLearningPathsHook/useStudentLearningPathsHook';
import userAuth from '../../hooks/userAuth';
import useStudentCoursesSavedOnlyIdHook from '../../hooks/useStudentCoursesSavedOnlyIdHook/useStudentCoursesSavedOnlyIdHook';
import { useMutationUpdateStudentCourseRecommendation } from '../../operations/mutations/student/useUpdateStudentCourseRecommendations/useUpdateStudentCourseRecommendations';
import {
  getPathwaysRefetch,
  getStudentPathwaysRefetch,
} from '../../operations/query/learningPaths/useGetLearningPathsInitial/useGetLearningPathsInitial';
import {
  getJobPostingRecommendationsRefetch,
  IGetJobPostingRecommendationsRefetchResponse,
} from '../../operations/query/students/useGetJobPostingRecommendations/useGetJobPostingRecomendations';
import {
  getCoursesRecommendationOnRefetch,
  getLearnerProfileOnChangeRefetch,
  IGetLearnerProfileOnChangeRefetch,
  IJobPreferencesRefetch,
  IJobTitleSkillSet,
  IRecommendedByMajorRefetch,
  IRecommendedOccupations,
  IStudentSkillForCoursesRecommendation,
} from '../../operations/query/students/useGetLearnerEntireProfile/useGetLearnerEntireProfile';
import { getJobRelatedOccupations } from '../../operations/query/students/useGetRelatedOccupations';
import { getCoursesSavedOnlyIdRefetch } from '../../operations/query/students/useGetStudentCoursesSavedOnlyCourseId/useGetStudentCoursesSavedOnlyCourseId';
import {
  getStudentKanbanSkillTrackerAllRefetch,
  IGetStudentKanbanSkillTrackerRefetchResponse,
} from '../../operations/query/students/useGetStudentKanbanSkillTrackerAll/useGetStudentKanbanSkilltrackerAll';
import { getStudentSkillSetChangeTriger } from '../../operations/query/students/useWhenStudentSkillSetChangeTrigger/useWhenStudentSkillSetChangeTrigger';
import { isVisualFeatureEnabled } from '../../utils/featureFlags';
import { ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';
import { FeatureFlagsContext } from '../FeatureFlagsContext';

export interface IStudentDataContextProvider {
  children: ReactNode;
}

export interface IStudentDataContext {
  refetchStudentData: () => void;
  refetchStudentDataWithoutKanban: () => void;
  refetchPathways: () => void;
  refetchStudentPathways: () => void;
  refetchCoursesSavedOnlyId: () => void;
  loading: boolean;
  incrementPendingOps: () => void;
  decrementPendingOps: () => void;
}

const StudentDataContextInitialState: IStudentDataContext = {
  refetchStudentData: () => null,
  refetchStudentDataWithoutKanban: () => null,
  refetchPathways: () => null,
  refetchStudentPathways: () => null,
  refetchCoursesSavedOnlyId: () => null,
  loading: false,
  incrementPendingOps: () => null,
  decrementPendingOps: () => null,
};

const timeConstant = 200;

const mergeSkillProficiencyLevel = (
  skill: IJobTitleSkillSetVar,
  serverSkillSet: IJobTitleSkillSet[],
): IJobTitleSkillSetVar => {
  const serverSkill = serverSkillSet.find((ss) => ss.skill === skill.skillId);

  if (!serverSkill) {
    return skill;
  }

  return { ...skill, skillProficiencyLevel: serverSkill.skillProficiencyLevel };
};

const mergeJobPrefSkillSet = (
  jobPref: IJobPreferencesVar,
  serverJobPrefs: IJobPreferencesRefetch[],
): IJobPreferencesVar => {
  const serverJobPref = serverJobPrefs.find((jp) => jp.jobTitleId === jobPref.jobTitleId);

  if (!serverJobPref) {
    return jobPref;
  }

  const mergedSkillSet = jobPref.jobTitleSkillSet.map((skill) =>
    mergeSkillProficiencyLevel(skill, serverJobPref.jobTitleSkillSet),
  );

  return { ...jobPref, jobTitleSkillSet: mergedSkillSet };
};

export const StudentDataContext = createContext<IStudentDataContext>(StudentDataContextInitialState);

const StudentDataContextProvider = ({ children }: IStudentDataContextProvider): JSX.Element => {
  const { auth } = userAuth();
  const { operations } = useJobPreferencesDataHook(jobPreferencesVar, kanbanDataVar);
  const { setRecommendedOccupationsByMajor } = RecommendedOccupationsByMajorHook(
    RecommendedOccupationsByMajorReactiveVar,
  );
  const { operations: kanbanOps } = useKanbanDataHook(
    kanbanDataVar,
    kanbanSkillType,
    kanbanJobTitleId,
    kanbanProficiencyLevel,
    operations.updateSkillProficiencyLevel,
  );
  const { setRecommendedOccupations } = RecommendedOccupationHook(RecommendedOccupationsReactiveVar);
  const { setRecommendedGoalsBySpecializedMajors } = RecommendedGoalsBySpecializedMajorsHook(
    RecommendedGoalsBySpecializedMajorsReactiveVar,
  );
  const { setSkillForCoursesRecommendations } = SkillForCoursesRecommendationHook();
  const { setRecommendedSkills } = useRecommendedSkillsHook(recommendedSkillsVar);
  const { operations: jobPostingRecommendationOps } = useJobPostingRecomnmendationHook();
  const { operations: studentCoursesSavedOnlyIdOps } = useStudentCoursesSavedOnlyIdHook();

  const studentIdRef = useRef<string | undefined>();
  const userCountryRef = useRef<string | undefined>();
  const userRoleRef = useRef<string | undefined>();
  const pendingOpsRef = useRef(0);
  const pendingOpsResolversRef = useRef<(() => void)[]>([]);
  const lastMutationCompleteTimeRef = useRef<number>(0);

  const incrementPendingOps = useCallback(() => {
    pendingOpsRef.current += 1;
  }, []);

  const decrementPendingOps = useCallback(() => {
    pendingOpsRef.current -= 1;
    lastMutationCompleteTimeRef.current = Date.now();
    if (pendingOpsRef.current <= 0) {
      pendingOpsRef.current = 0;
      pendingOpsResolversRef.current.forEach((resolve) => resolve());
      pendingOpsResolversRef.current = [];
    }
  }, []);

  const waitForPendingOps = async (): Promise<void> => {
    if (pendingOpsRef.current > 0) {
      await new Promise<void>((resolve) => {
        pendingOpsResolversRef.current.push(resolve);
      });
    }

    const minDelayAfterMutation = 150;
    const timeSinceLastMutation = Date.now() - lastMutationCompleteTimeRef.current;

    if (timeSinceLastMutation < minDelayAfterMutation) {
      await new Promise((resolve) => {
        setTimeout(resolve, minDelayAfterMutation - timeSinceLastMutation);
      });
    }
  };

  const { operations: learningPathsByInstitutionOps } =
    useLearningPathsByInstitutionHook(learningPathsByInstitutionVar);
  const { operations: learningPathsByObjectivesOps } = useLearningPathsByObjectivesHook(learningPathsByObjectivesVar);
  const { operations: learningPathsOps } = useStudentLearningPathsHook(studentLearningPathsVar);
  const [refetchLoading, setRefetchLoading] = useState<boolean>(false);
  const [updateStudentCoursesRecommendation] = useMutationUpdateStudentCourseRecommendation();
  const { features } = useContext(FeatureFlagsContext);
  const visualFeatures = features.qsemVisualFeatures;
  const qsemLearnerShowJobPostingRecommendations = isVisualFeatureEnabled(
    visualFeatures,
    FeaturesEnum.qsemLearnerShowJobPostingRecommendations,
  );
  const isSpecializedMajorsEnabledRef = useRef(false);
  const defaultAcademicProgramIdRef = useRef<string | undefined>();

  isSpecializedMajorsEnabledRef.current = features?.qsemMajorsEnableSpecializedMajors === true;
  defaultAcademicProgramIdRef.current = features?.qsemDefaultAcademicProgram?.id;

  const refreshRecommendedGoalsBySpecializedMajors = async (studentId: string): Promise<void> => {
    if (!isSpecializedMajorsEnabledRef.current) {
      return;
    }

    const recommendedGoals = await fetchRecommendedGoalsBySpecializedMajors({
      studentId,
      defaultAcademicProgramId: defaultAcademicProgramIdRef.current,
    });

    if (recommendedGoals?.length) {
      setRecommendedGoalsBySpecializedMajors(recommendedGoals);
    }
  };

  useEffect(() => {
    studentIdRef.current = auth?.student?.id;
    userCountryRef.current = auth?.country;
    userRoleRef.current = auth?.userRole;
  }, [auth]);

  const setJobPreferences = (jobPreferences: IJobPreferencesRefetch[]): void => {
    operations.setJobPreferences(
      jobPreferences.map((jobPreference) => ({
        jobTitleId: jobPreference.jobTitleId,
        jobTitleName: jobPreference.jobTitleName,
        match: 0,
        studentId: jobPreference.studentId,
        jobTitleSkillSet: jobPreference.jobTitleSkillSet,
      })),
    );
  };

  const setRecommendedOccupationsByMajorData = (recommendedOccupationsByMajor: IRecommendedByMajorRefetch[]): void => {
    setRecommendedOccupationsByMajor(
      recommendedOccupationsByMajor.map((recommendedMajor) => ({
        majorId: recommendedMajor.studentMajor.tblMajor?.id ?? '',
        majorName: recommendedMajor.studentMajor.tblMajor?.name ?? '',
        match: recommendedMajor.percentageOfMajorMatch.map((percentage) => ({
          name: percentage.name,
          match: percentage.match,
          jobTitleId: percentage.id,
        })),
      })),
    );
  };

  const setRecommendedOccupationsData = (recommendedOccupation: IRecommendedOccupations[]): void => {
    setRecommendedOccupations(
      recommendedOccupation.map((jobTitle) => ({
        name: jobTitle.job,
        match: jobTitle.match,
        jobTitleId: jobTitle.id,
      })),
    );
  };

  const setStudentSkillForCoursesRecommendation = (
    skillCoursesRecommendation: IStudentSkillForCoursesRecommendation[] | undefined,
  ): void => {
    if (!skillCoursesRecommendation) {
      return;
    }
    setSkillForCoursesRecommendations(
      skillCoursesRecommendation.map((sc) => ({
        ...sc,
      })),
    );
  };

  const handleSkillSetChangeTrigger = async (studentId: string): Promise<boolean> => {
    const { error } = await getStudentSkillSetChangeTriger(studentId, false, userCountryRef.current);

    return !!error;
  };

  const fetchAllData = async (
    studentId: string,
  ): Promise<
    [
      IGetLearnerProfileOnChangeRefetch,
      IGetStudentKanbanSkillTrackerRefetchResponse,
      IGetJobPostingRecommendationsRefetchResponse | undefined,
    ]
  > =>
    Promise.all([
      getLearnerProfileOnChangeRefetch(studentId),
      getStudentKanbanSkillTrackerAllRefetch(['specialized', 'human'], studentId),
      qsemLearnerShowJobPostingRecommendations
        ? getJobPostingRecommendationsRefetch(jobTitleIdForJobPostings())
        : undefined,
    ]);

  const handleKanbanData = (kanbanResult: IGetStudentKanbanSkillTrackerRefetchResponse): void => {
    if (!kanbanResult.error && kanbanResult.data.getStudentKanbanSkilltrackerAll) {
      kanbanOps.setKanbanData(kanbanResult.data.getStudentKanbanSkilltrackerAll);
    }
  };

  const handleJobPostingRecommendations = (
    jobPostingRecommendationData: IGetJobPostingRecommendationsRefetchResponse | undefined,
  ): void => {
    if (jobPostingRecommendationData?.data?.getJobPostingRecommendations?.length) {
      jobPostingRecommendationOps.setJobPostingRecommendations(
        jobPostingRecommendationData.data.getJobPostingRecommendations,
      );
    }
  };

  const valueContext = useMemo(
    () => ({
      refetchStudentData: throttle(async () => {
        if (!studentIdRef.current || userRoleRef.current !== ROLE_ENUM.STUDENT) {
          return;
        }

        await waitForPendingOps();

        setRefetchLoading(true);

        const skillSetChangeError = await handleSkillSetChangeTrigger(studentIdRef.current);

        if (skillSetChangeError) {
          setRefetchLoading(false);

          return;
        }

        const [[{ data: learnerData, error: learnerError }, kanbanResult, jobPostingRecommendationData]] =
          await Promise.all([
            fetchAllData(studentIdRef.current),
            refreshRecommendedGoalsBySpecializedMajors(studentIdRef.current),
          ]);

        handleKanbanData(kanbanResult);

        if (!learnerError) {
          const { jobPreferences, recommendationsByMajor, recommendedOccupations, recommendedSkills } =
            learnerData.getLearnerEntireProfile;

          setRecommendedSkills(recommendedSkills);

          const jobTitleIds = jobPreferences.map(({ jobTitleId }) => jobTitleId) || [];

          setJobPreferences(jobPreferences);

          setRecommendedOccupationsByMajorData(recommendationsByMajor);
          setRecommendedOccupationsData(recommendedOccupations);

          const { data: relatedQueryData, error: relatedError } = await getJobRelatedOccupations(jobTitleIds);

          setRefetchLoading(false);

          if (relatedError) {
            setRefetchLoading(false);

            return;
          }

          const relatedData = relatedQueryData?.relatedOccupations;

          if (relatedData) {
            RelatedOccupationsReactiveVar({ error: '', data: relatedData });
          }
        }
        setRefetchLoading(false);
        await updateStudentCoursesRecommendation({
          variables: {
            studentId: studentIdRef.current,
          },
        });

        const responseCourse = await getCoursesRecommendationOnRefetch(studentIdRef.current);

        setStudentSkillForCoursesRecommendation(
          responseCourse.data?.getLearnerEntireProfile?.studentSkillForCoursesRecommendation,
        );
        handleJobPostingRecommendations(jobPostingRecommendationData);
      }, timeConstant),
      refetchStudentDataWithoutKanban: throttle(async () => {
        if (!studentIdRef.current || userRoleRef.current !== ROLE_ENUM.STUDENT) {
          return;
        }

        await waitForPendingOps();

        setRefetchLoading(true);

        const skillSetChangeError = await handleSkillSetChangeTrigger(studentIdRef.current);

        if (skillSetChangeError) {
          setRefetchLoading(false);

          return;
        }

        const currentJobPreferences = jobPreferencesVar();
        const jobTitleIds = currentJobPreferences.data.map(({ jobTitleId }) => jobTitleId) || [];

        const [recommendedSkillsResult, recommendationsResult, jobPostingRecommendationData] = await Promise.all([
          getCoursesRecommendationOnRefetch(studentIdRef.current),
          getLearnerProfileOnChangeRefetch(studentIdRef.current),
          qsemLearnerShowJobPostingRecommendations
            ? getJobPostingRecommendationsRefetch(jobTitleIdForJobPostings())
            : undefined,
          refreshRecommendedGoalsBySpecializedMajors(studentIdRef.current),
        ]);

        if (!recommendationsResult.error) {
          const {
            recommendationsByMajor,
            recommendedOccupations,
            recommendedSkills,
            jobPreferences: refetchedJobPreferences,
          } = recommendationsResult.data.getLearnerEntireProfile;

          setRecommendedSkills(recommendedSkills);
          setRecommendedOccupationsByMajorData(recommendationsByMajor);
          setRecommendedOccupationsData(recommendedOccupations);

          if (refetchedJobPreferences?.length) {
            const currentJobPrefs = jobPreferencesVar();
            const mergedData = currentJobPrefs.data.map((jobPref) =>
              mergeJobPrefSkillSet(jobPref, refetchedJobPreferences),
            );

            operations.setJobPreferencesOrganized(mergedData);
          }

          const { data: relatedQueryData, error: relatedError } = await getJobRelatedOccupations(jobTitleIds);

          if (!relatedError && relatedQueryData?.relatedOccupations) {
            RelatedOccupationsReactiveVar({ error: '', data: relatedQueryData.relatedOccupations });
          }
        }

        setStudentSkillForCoursesRecommendation(
          recommendedSkillsResult.data?.getLearnerEntireProfile?.studentSkillForCoursesRecommendation,
        );

        handleJobPostingRecommendations(jobPostingRecommendationData);

        setRefetchLoading(false);

        await updateStudentCoursesRecommendation({
          variables: {
            studentId: studentIdRef.current,
          },
        });
      }, timeConstant),
      refetchPathways: throttle(async () => {
        if (studentIdRef.current && userRoleRef.current === ROLE_ENUM.STUDENT) {
          const { data: pathwaysData, error: pathwaysError } = await getPathwaysRefetch();

          if (pathwaysError) {
            return;
          }

          if (pathwaysData?.getPathwaysByInstitutionAndOccupations) {
            learningPathsByInstitutionOps.setLearningPathsByInstitution(
              pathwaysData?.getPathwaysByInstitutionAndOccupations,
            );
          }
          if (pathwaysData?.getPathwaysByObjectives) {
            learningPathsByObjectivesOps.setLearningPathsByObjectives(pathwaysData?.getPathwaysByObjectives);
          }
        }
      }, timeConstant),
      refetchStudentPathways: throttle(async () => {
        if (studentIdRef.current && userRoleRef.current === ROLE_ENUM.STUDENT) {
          const { data: studentPathwaysData, error: studentPathwaysError } = await getStudentPathwaysRefetch();

          if (studentPathwaysError) {
            return;
          }

          if (studentPathwaysData?.getStudentPathways) {
            learningPathsOps.setStudentLearningPaths(studentPathwaysData.getStudentPathways);
          }
        }
      }, timeConstant),
      refetchCoursesSavedOnlyId: throttle(async () => {
        if (studentIdRef.current && userRoleRef.current === ROLE_ENUM.STUDENT) {
          const { data: coursesSavedOnlyIdData, error: coursesSavedOnlyIdError } = await getCoursesSavedOnlyIdRefetch();

          if (coursesSavedOnlyIdError) {
            return;
          }
          if (coursesSavedOnlyIdData?.getCoursesSaved?.coursesSaved) {
            studentCoursesSavedOnlyIdOps.handleSetCoursesSavedOnlyId(
              coursesSavedOnlyIdData?.getCoursesSaved?.coursesSaved,
            );
          }
        }
      }, timeConstant),
      loading: refetchLoading,
      incrementPendingOps,
      decrementPendingOps,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [studentIdRef, refetchLoading, incrementPendingOps, decrementPendingOps],
  );

  return <StudentDataContext.Provider value={valueContext}>{children}</StudentDataContext.Provider>;
};

export default StudentDataContextProvider;
